// ============================================================
// INTENTS - what the players mean to do, queued for the GM.
//
// Outside the rounds there is no turn order to say whose word comes next.
// A player writes what they intend ("I search the desk while she is
// talking") and it joins a queue on the GM's screen, oldest first, until
// the GM has dealt with it. It is an `asks` document addressed to the GM:
// answering it takes it off both screens, and the player may withdraw it.
//
// Nothing here is rolled or resolved: an intent is a sentence.
//
// Pure. Zero React dependency.
// ============================================================

/** The ask's kind on the wire. */
export const INTENT_ASK = 'intent';

export const MAX_INTENT = 240;

export interface Intent {
  /** What they mean to do, in their words. */
  text: string;
  /** Who, as the table knows them: the character they act as, else the player. */
  name: string;
}

/** An intent from what was typed, or null when nothing was. */
export function intentOf(text: string, name: string): Intent | null {
  const said = text.trim().slice(0, MAX_INTENT);
  return said ? { text: said, name: name.trim() || 'Someone' } : null;
}

/** An intent that crossed the wire, validated; null when it says nothing. */
export function readIntent(raw: unknown): Intent | null {
  const stored = (raw ?? {}) as { text?: unknown; name?: unknown };
  if (typeof stored.text !== 'string') return null;
  return intentOf(stored.text, typeof stored.name === 'string' ? stored.name : '');
}

interface StoredAsk {
  id: string;
  kind: string;
  payload: unknown;
  createdAt?: number;
}

/** The intents among these asks, oldest first, each with the ask it rides. */
export function intentsIn(asks: readonly StoredAsk[]): { id: string; intent: Intent }[] {
  return asks
    .filter((a) => a.kind === INTENT_ASK)
    .map((a) => ({ id: a.id, intent: readIntent(a.payload), at: a.createdAt ?? 0 }))
    .filter((row): row is { id: string; intent: Intent; at: number } => row.intent !== null)
    .sort((a, b) => a.at - b.at)
    .map(({ id, intent }) => ({ id, intent }));
}
